import React from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_AVATAR = "https://cdn.geo.elelem.ai/user.png";

const getInitials = (firstname, lastname, email) => {
  if (firstname && lastname) {
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
  }
  if (firstname) {
    return firstname.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "U";
};

const formatRoleLabel = (role, t) => {
  if (role === "admin" || role === "super-admin") {
    return t("members.roleAdmin", "Admin");
  }
  return t("members.roleMember", "Member");
};

const MembersTable = ({
  members = [],
  loading = false,
  sortField = "name",
  sortDirection = "asc",
  onSort,
}) => {
  const { t } = useTranslation();

  const handleHeaderClick = (field) => {
    if (typeof onSort === "function") {
      onSort(field);
    }
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return (
      <span className="sort-icon" aria-hidden="true">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const getAriaSort = (field) => {
    if (sortField !== field) return "none";
    return sortDirection === "asc" ? "ascending" : "descending";
  };

  if (loading) {
    return (
      <div className="members-table-card">
        <div className="members-empty-state">
          <div className="spinner-border text-info mb-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>{t("members.loadingMembers", "Loading members...")}</div>
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="members-table-card">
        <div className="members-empty-state">
          <i className="bi bi-people members-empty-icon" aria-hidden="true" />
          <div className="fw-semibold text-secondary">
            {t("members.noMembersFound", "No members match your search or filter criteria")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="members-table-card">
      <table className="members-table" data-testid="members-table">
        <thead>
          <tr>
            <th aria-sort={getAriaSort("name")}>
              <button
                type="button"
                className="table-sort-btn"
                onClick={() => handleHeaderClick("name")}
                data-testid="sort-name"
              >
                <span>{t("members.name", "Name")}</span>
                {renderSortIndicator("name")}
              </button>
            </th>
            <th aria-sort={getAriaSort("email")}>
              <button
                type="button"
                className="table-sort-btn"
                onClick={() => handleHeaderClick("email")}
                data-testid="sort-email"
              >
                <span>{t("members.email", "Email")}</span>
                {renderSortIndicator("email")}
              </button>
            </th>
            <th aria-sort={getAriaSort("role")}>
              <button
                type="button"
                className="table-sort-btn"
                onClick={() => handleHeaderClick("role")}
                data-testid="sort-status"
              >
                <span>{t("members.status", "Status")}</span>
                {renderSortIndicator("role")}
              </button>
            </th>
            <th aria-sort={getAriaSort("teams")}>
              <button
                type="button"
                className="table-sort-btn"
                onClick={() => handleHeaderClick("teams")}
                data-testid="sort-teams"
              >
                <span>{t("members.teams", "Teams")}</span>
                {renderSortIndicator("teams")}
              </button>
            </th>
            <th aria-sort={getAriaSort("joined")}>
              <button
                type="button"
                className="table-sort-btn"
                onClick={() => handleHeaderClick("joined")}
                data-testid="sort-joined"
              >
                <span>{t("members.joined", "Joined")}</span>
                {renderSortIndicator("joined")}
              </button>
            </th>
            <th aria-sort={getAriaSort("last_seen")}>
              <button
                type="button"
                className="table-sort-btn"
                onClick={() => handleHeaderClick("last_seen")}
                data-testid="sort-last-seen"
              >
                <span>{t("members.lastSeen", "Last seen")}</span>
                {renderSortIndicator("last_seen")}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const displayName =
              member.firstname && member.lastname
                ? `${member.firstname} ${member.lastname}`
                : member.firstname || member.email?.split("@")[0] || "User";

            const usernameHandle =
              member.username || member.email?.split("@")[0] || "";

            const role = member.role || "user";
            const isAdmin = role === "admin" || role === "super-admin";
            const roleBadgeClass = isAdmin
              ? "role-badge role-admin"
              : "role-badge role-member";

            const projectsList = Array.isArray(member.projects)
              ? member.projects
              : member.client_id
                ? [member.client_id]
                : [];
            const teamsCount = Math.max(1, projectsList.length);

            const isOnline = Boolean(member.is_online);
            const lastSeenLabel = isOnline
              ? t("members.online", "Online")
              : member.last_seen || t("members.recently", "Recently");

            const avatarUrl = member.profile_picture || DEFAULT_AVATAR;

            return (
              <tr key={member.username || member.email} data-testid={`member-row-${usernameHandle}`}>
                {/* 1. Name */}
                <td>
                  <div className="member-profile-cell">
                    <div className="member-avatar-box">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={displayName}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display = "flex";
                            }
                          }}
                        />
                      ) : null}
                      <span
                        className="member-avatar-initials"
                        style={{ display: avatarUrl ? "none" : "flex" }}
                      >
                        {getInitials(member.firstname, member.lastname, member.email)}
                      </span>
                    </div>
                    <div className="member-info">
                      <span className="member-display-name">{displayName}</span>
                      {usernameHandle && (
                        <span className="member-handle">{usernameHandle}</span>
                      )}
                    </div>
                  </div>
                </td>

                {/* 2. Email */}
                <td>
                  <a
                    href={`mailto:${member.email}`}
                    className="member-email"
                    title={member.email}
                  >
                    {member.email}
                  </a>
                </td>

                {/* 3. Status */}
                <td>
                  <span className={roleBadgeClass}>
                    {formatRoleLabel(role, t)}
                  </span>
                </td>

                {/* 4. Teams */}
                <td>
                  <span
                    className="teams-badge"
                    title={projectsList.join(", ")}
                  >
                    {teamsCount === 1
                      ? t("members.teamsCount_one", "{{count}} team", { count: 1 })
                      : t("members.teamsCount_other", "{{count}} teams", { count: teamsCount })}
                  </span>
                </td>

                {/* 5. Joined */}
                <td>
                  <span>{member.joined || "—"}</span>
                </td>

                {/* 6. Last Seen */}
                <td>
                  <div className="presence-indicator">
                    <span
                      className={`presence-dot ${isOnline ? "online" : ""}`}
                      aria-hidden="true"
                    />
                    <span className={`presence-text ${isOnline ? "online" : ""}`}>
                      {lastSeenLabel}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MembersTable;
